import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Head from '@/components/feature/Head';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import Pagination from '@/components/feature/Pagination';
import { mangaList, mangaCategories } from '@/mocks/manga';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const activeCategory = mangaCategories.find((c) => c.slug === slug) || mangaCategories[0];

  const filtered = useMemo(() => {
    let list = slug === 'all' ? mangaList : mangaList.filter((m) => m.categorySlug === slug);

    switch (sortBy) {
      case 'popular':
        return [...list].sort((a, b) => b.views - a.views);
      case 'rating':
        return [...list].sort((a, b) => b.rating - a.rating);
      default:
        return [...list].sort(
          (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
        );
    }
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const pageItems = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const navigate = useNavigate();

  const handleCategoryChange = (catSlug: string) => {
    navigate(`/category/${catSlug}`);
    setCurrentPage(1);
  };

  const categoryTitle = slug === 'all' ? '全部分类' : activeCategory.name;
  const categoryDescription = slug === 'all'
    ? '浏览H漫画全部分类，发现海量优质H漫画资源。涵盖日系漫画、韩系漫画、国产原创、同人志等多种风格与题材。'
    : `浏览${activeCategory.name}类H漫画资源，海量优质${activeCategory.name}作品每日更新，支持在线阅读与下载。`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/all' },
    { label: categoryTitle },
  ];

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryTitle} - H漫画在线阅读`,
    description: categoryDescription,
    url: `https://hahamanga.github.io/category/${slug}`,
  };

  return (
    <>
      <Head
        title={`${categoryTitle} - H漫画在线阅读 | H漫画`}
        description={categoryDescription}
        keywords={`${categoryTitle},H漫画,H漫画在线阅读,最新H漫画,H漫画下载`}
        canonical={`https://hahamanga.github.io/category/${slug}`}
        ogTitle={`${categoryTitle} - H漫画在线阅读`}
        ogDescription={categoryDescription}
        ogUrl={`https://hahamanga.github.io/category/${slug}`}
        jsonLd={[webPageSchema]}
      />

      <div className="min-h-screen pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100">
              {categoryTitle}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 <strong className="text-primary-500 font-semibold">{filtered.length}</strong> 部作品
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-6 pb-4 border-b border-background-200/50">
            <div className="flex items-center gap-1.5 flex-wrap">
              {mangaCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-400 hover:text-foreground-200 bg-background-100 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              第{currentPage}页 / 共{totalPages}页
            </p>
            <div className="flex items-center gap-2">
              {[
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '人气' },
                { key: 'rating', label: '评分' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => {
                    setSortBy(s.key as typeof sortBy);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-primary-500/10 text-primary-500 font-medium'
                      : 'text-foreground-400 hover:text-foreground-200'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
                {pageItems.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-2xl text-foreground-500 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-foreground-500">该分类暂无作品</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}